import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  getModeStatus,
  abandonPuzzle as abandonPuzzleApi,
  submitSolution as submitSolutionApi,
  findPuzzle as findPuzzleApi,
  findPuzzleByChallenge as findPuzzleByChallengeApi,
  startPuzzle as startPuzzleApi,
  rejectPuzzle as rejectPuzzleApi,
  joinTournamentByShareToken,
} from '../services/gameApi';
import { tournamentFindPuzzle, createBlank, createSolved, createAuto, claimTokens as claimTokensApi, editCreatedPuzzle as editCreatedPuzzleApi, soloFindByShareCode } from '../services/api';
import { useGlobalError } from './GlobalErrorContext';
import { useMetadata } from './MetadataContext';
import { useAd } from './AdContext'; 
import storage from '../platform/storage';

const GameStateContext = createContext(null);

export const GameStateProvider = ({ children }) => {
  const { metadata, hydrated: metadataHydrated } = useMetadata();
  const { checkAndHandleAdRequirement } = useAd();
  
  // null until the stored mode is loaded; App.js holds rendering until
  // `hydrated` is true
  const [activeMode, setActiveMode] = useState(null);
  const [hydrated, setHydrated] = useState(false);
  
  const [gameData, setGameData] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [showCountdown, setShowCountdown] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const isFetchingRef = useRef(false);

  // Load the stored mode on mount
  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      const savedMode = await storage.getItem('activeMode');
      if (cancelled) return;
      // metadata is validated by the effect below once it is loaded
      const mode = savedMode || 'solo';
      if (!savedMode) {
        storage.setItem('activeMode', mode);
      }
      setActiveMode(mode);
      setHydrated(true);
    };

    hydrate();
    return () => { cancelled = true; };
  }, []);

  // Re-validate activeMode when metadata loads/changes
  useEffect(() => {
    // before hydration activeMode is null, which would overwrite the stored mode
    if (!hydrated) return;
    if (metadata?.modes) {
      const validModes = metadata.modes.map(mode => mode.code);
      if (!validModes.includes(activeMode)) {
        const defaultMode = validModes[0] || 'solo';
        setActiveMode(defaultMode);
        storage.setItem('activeMode', defaultMode);
      }
    }
  }, [hydrated, metadata, activeMode]);

  const fetchStatus = useCallback(async (mode = activeMode) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const data = await getModeStatus(mode);
      setGameData(data);
      // Extract token balance from the response
      if (data && typeof data.token_balance === 'number') {
        setTokenBalance(data.token_balance);
      }
      // Extract unread messages count from the response
      if (data && typeof data.unread_messages === 'number') {
        setUnreadMessages(data.unread_messages);
      }
    } catch (error) {
      // Let API layer handle all error display
      console.error('Fetch status failed:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeMode]);

  // Re-fetch the active mode's status (mobile: when the app returns to the
  // foreground). Waits for hydration like the initial fetch, and shares the
  // isFetchingRef guard, so a call while a fetch is in flight is dropped
  const refreshStatus = useCallback(async () => {
    if (!hydrated || !metadataHydrated || !activeMode) return;
    await fetchStatus(activeMode);
  }, [hydrated, metadataHydrated, activeMode, fetchStatus]);

  const updateStatus = useCallback((data) => {
    setGameData(data);
    // Extract token balance from the response
    if (data && typeof data.token_balance === 'number') {
      setTokenBalance(data.token_balance);
    }
    // Extract unread messages count from the response
    if (data && typeof data.unread_messages === 'number') {
      setUnreadMessages(data.unread_messages);
    }
  }, []);

  const changeMode = useCallback(async (mode) => {
    if (mode === activeMode) {
      return;
    }
    
    // Validate mode against metadata if available
    if (metadata?.modes) {
      const validModes = metadata.modes.map(m => m.code);
      if (!validModes.includes(mode)) {
        console.warn(`Invalid mode "${mode}" rejected. Valid modes:`, validModes);
        return;
      }
    }
    
    // Clear game data before switching modes to prevent showing wrong component
    setGameData(null);
    setActiveMode(mode);
    storage.setItem('activeMode', mode);
    
    await fetchStatus(mode);
  }, [activeMode, fetchStatus, metadata]);

  const abandonPuzzle = useCallback(async () => {
    try {
      const data = await abandonPuzzleApi(activeMode);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Abandon puzzle failed:', error);
    }
  }, [activeMode, updateStatus]);

  const submitSolution = useCallback(async () => {
    try {
      const data = await submitSolutionApi(activeMode);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Submit solution failed:', error);
    }
  }, [activeMode, updateStatus]);

  const findPuzzle = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const data = await findPuzzleApi(activeMode);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Find puzzle failed:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeMode, updateStatus]);


  const findTournamentPuzzle = useCallback(async (tournamentKey) => {
    if (!tournamentKey) return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      // Make sure we're in tournament mode
      if (activeMode !== 'tournament') {
        setActiveMode('tournament');
        storage.setItem('activeMode', 'tournament');
      }
      
      const data = await tournamentFindPuzzle(tournamentKey);
      // Update the game state with the tournament puzzle data
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Find tournament puzzle failed:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeMode, setActiveMode, updateStatus]);

  const joinTournament = useCallback(async (shareToken) => {
    if (!shareToken) return;
    
    try {
      const data = await joinTournamentByShareToken(shareToken);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Join tournament failed:', error);
      throw error; // Re-throw so the calling component can handle it
    }
  }, []);

  const findPuzzleByChallenge = useCallback(async (challengeCode) => {
    if (!challengeCode) return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      // Switch to duel mode first
      if (activeMode !== 'duel') {
        setActiveMode('duel');
        storage.setItem('activeMode', 'duel');
      }
      
      // Find the puzzle by challenge code
      const data = await findPuzzleByChallengeApi('duel', challengeCode);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Find challenge puzzle failed:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeMode, setActiveMode, updateStatus]);

  const findPuzzleByShareCode = useCallback(async (shareCode) => {
    if (!shareCode) return;
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      // Switch to solo mode first
      if (activeMode !== 'solo') {
        setActiveMode('solo');
        storage.setItem('activeMode', 'solo');
      }
      
      // Find the puzzle by share code
      const data = await soloFindByShareCode(shareCode);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Find shared puzzle failed:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeMode, setActiveMode, updateStatus]);

  const createPuzzle = useCallback(async (createType, style, size) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      // Switch to create mode first
      if (activeMode !== 'create') {
        setActiveMode('create');
        storage.setItem('activeMode', 'create');
      }
      
      let data;
      switch (createType) {
        case 'blank':
          data = await createBlank(style, size);
          break;
        case 'solved':
          data = await createSolved(style, size);
          break;
        case 'auto':
          data = await createAuto(style, size);
          break;
        default:
          throw new Error(`Invalid createType: ${createType}`);
      }
      
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Create puzzle failed:', error);
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeMode, setActiveMode, updateStatus]);

  const editCreatedPuzzle = useCallback(async (puzzleKey) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    
    try {
      // Switch to create mode first
      if (activeMode !== 'create') {
        setActiveMode('create');
        storage.setItem('activeMode', 'create');
      }
      
      const data = await editCreatedPuzzleApi(puzzleKey);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Edit puzzle failed:', error);
      throw error;
    } finally {
      isFetchingRef.current = false;
    }
  }, [activeMode, setActiveMode, updateStatus]);

  const startPuzzleDirectly = useCallback(async () => {
    try {
      let data;
      // Wrap the start puzzle API call with ad requirement checking
      await checkAndHandleAdRequirement(async () => {
        data = await startPuzzleApi(activeMode);
      });
      
      // Update the game status after successful start
      updateStatus(data);
    } catch (error) {
      // Don't show error for ad-related failures (402 errors or ad failures)
      // These are handled by the ad system and EarnTokenModal
      if (error.response?.status === 402 || error.message?.includes('Ad failed')) {
        console.log('Ad-related error handled by ad system:', error.message);
        return;
      }
      
      // Let API layer handle all error display
      console.error('Start puzzle failed:', error);
    }
  }, [activeMode, updateStatus, checkAndHandleAdRequirement]);

  const startPuzzle = useCallback(async () => {
    setShowCountdown(true);
  }, []);


  const handleCountdownComplete = useCallback(async () => {
    setShowCountdown(false);
    setIsStarting(true);
    await startPuzzleDirectly();
    setIsStarting(false);
  }, [startPuzzleDirectly]);

  const rejectPuzzle = useCallback(async () => {
    try {
      const data = await rejectPuzzleApi(activeMode);
      updateStatus(data);
    } catch (error) {
      // Let API layer handle all error display
      console.error('Reject puzzle failed:', error);
    }
  }, [activeMode, updateStatus]);

  const claimTokens = useCallback(async () => {
    try {
      const data = await claimTokensApi();
      // Update token balance from the response
      if (data && typeof data.token_balance === 'number') {
        setTokenBalance(data.token_balance);
      }
      return data;
    } catch (error) {
      // Let API layer handle all error display
      console.error('Claim tokens failed:', error);
      throw error; // Re-throw so the calling component can handle it
    }
  }, []);

  // Initialize game state once the stored mode is loaded, and the metadata
  // version too, so the first request carries x-metadata-version
  useEffect(() => {
    if (hydrated && metadataHydrated && activeMode) {
      fetchStatus(activeMode);
    }
  }, [hydrated, metadataHydrated]); // Only run once, when both have loaded

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => {
    const value = {
      hydrated,
      activeMode,
      gameData,
      tokenBalance,
      unreadMessages,
      showCountdown,
      isStarting,
      changeMode,
      abandonPuzzle,
      submitSolution,
      findPuzzle,
      findPuzzleByChallenge,
      findPuzzleByShareCode,
      findTournamentPuzzle,
      joinTournament,
      createPuzzle,
      editCreatedPuzzle,
      startPuzzle,
      rejectPuzzle,
      claimTokens,
      handleCountdownComplete,
      refreshStatus
    };

    return value;
  }, [hydrated, activeMode, gameData, tokenBalance, unreadMessages, showCountdown, isStarting, changeMode, abandonPuzzle, submitSolution, findPuzzle, findPuzzleByChallenge, findPuzzleByShareCode, findTournamentPuzzle, joinTournament, createPuzzle, editCreatedPuzzle, startPuzzle, rejectPuzzle, claimTokens, handleCountdownComplete, refreshStatus]);

  return (
    <GameStateContext.Provider value={contextValue}>
      {children}
    </GameStateContext.Provider>
  );
};

export const useGameState = () => {
  const context = useContext(GameStateContext);
  if (!context) {
    throw new Error('useGameState must be used within a GameStateProvider');
  }
  return context;
};
